import { errorMessageFactory, getApiValidationErrors } from 'factories/api';
import { hourDraftFactory } from 'factories/hours';
import { omit, remove } from 'lodash-es';
import { DateTime } from 'luxon';
import type { HourDraftSeed } from 'redux/modules/hours.actions';

// Lazy import to break circular dependency: forecast.actions → hours.actions → projects.actions → forecast.actions
const getHoursActions = () => import('redux/modules/hours.actions');

import {
  fetchFullProjectsWithUsersIfNeeded,
  fetchInitialProjects,
} from 'redux/modules/projects.actions';
import { showToast } from 'redux/modules/toasts.actions';
import { addUserToProject } from 'redux/modules/user.editor.actions';
import { getMaybeHasMoreStatus } from 'redux/reusable/pagination';
import { xhrStatusQueryInfo } from 'redux/reusable/xhrStatus';
import { getAccountFeatures } from 'redux/selectors/accounts';
import { getCalendarUserId } from 'redux/selectors/calendar.filter';
import { getProjectById } from 'redux/selectors/projects';
import { getIsRouteCalendar } from 'redux/selectors/routing';
import { getUser, getUserPermissions } from 'redux/selectors/user';
import { getUserEntities } from 'redux/selectors/users';
import { extractProjectBase } from 'redux/selectors/utils/projects';
import type { ForecastFilter } from 'routes/forecast/api/forecast.api';
import { forecastApi } from 'routes/forecast/api/forecast.api';
import { generateForecastDraft } from 'routes/forecast/factories/forecast';
import type { Forecast, ForecastDraft } from 'routes/forecast/types/forecast';
import type { HourAnalyticsContext, TaskAnalyticsContext } from 'types/analytics';
import type { EnrichedHourDraft } from 'types/hour';
import type { Thunk } from 'types/redux';
import { finalizeTaskAnalyticsContext } from 'utils/analytics-context';
import { fetchPaginated } from 'utils/api';
import { getNextInstanceOfISODay, isAfterDay } from 'utils/date-helper';
import { calculateDuration, durationFromHours } from 'utils/durations';
import { notNull, pick } from 'utils/typesafe';
import { PROJECT_ASSIGNEES_FILTER_LIMIT } from './forecast.constants';
import {
  getEnrichedForecasts,
  getFilteredForecastUsers,
  getForecastCalendarFilter,
  getForecastDraft,
  getForecastEntities,
  getForecastFetchKeys,
  getForecastPagination,
  getForecastQuery,
  getForecastQueryUserIds,
  getForecastSelections,
  getForecasts,
  getRawXhrStatus,
} from './forecast.selectors';
import type { ForecastStorageKeys } from './forecast.types';
import * as types from './forecast.types';
import {
  batchSelectionActions,
  clearForecastDraft,
  clearQuery,
  deleteForecastIds,
  FORECAST_PAGE_LIMIT,
  fetchedForecasts,
  forecastXhrModule,
  paginationActions,
} from './forecast.types';

const { wrapFetching, wrapSaving } = forecastXhrModule;

export function fetchForecastsSmartly(
  partialFilter?: Partial<ForecastFilter>,
  fetchKey?: string
): Thunk<Promise<void>> {
  return (dispatch, getState) => {
    const rawXhrStatus = getRawXhrStatus(getState());
    const xhrStatus = xhrStatusQueryInfo(rawXhrStatus, { key: fetchKey, maxMinuteAge: 1 });

    if (xhrStatus.shouldFetch) {
      return dispatch(fetchForecasts(partialFilter, fetchKey));
    }
    return Promise.resolve();
  };
}

export function fetchForecasts(
  partialFilter?: Partial<ForecastFilter>,
  fetchKey?: string
): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const hasTasksFeature = getAccountFeatures(getState()).tasks;

    if (!hasTasksFeature) {
      return Promise.resolve();
    }

    const wantToFetch = partialFilter?.limit ?? 100;
    const perPage = FORECAST_PAGE_LIMIT;
    const page = partialFilter?.page ?? 1;
    const pages = Math.ceil(wantToFetch / perPage);
    const pageOffset = (page - 1) * pages;

    return dispatch(
      fetchForecastsInBatches(
        [{ ...partialFilter, page: pageOffset + page, limit: perPage }],
        fetchKey
      )
    );
  };
}

export function fetchForecastsInBatches(
  filters: Array<Partial<ForecastFilter>>,
  fetchKey?: string
): Thunk<Promise<void>> {
  console.log('fetchForecastsInBatches called with filters:', filters);
  return async dispatch => {
    try {
      const perPage = FORECAST_PAGE_LIMIT;
      await dispatch(
        wrapFetching(
          Promise.all(
            filters.map(filter => {
              return fetchPaginated({ perPage }, async page => {
                const forecasts = await forecastApi.getForecasts({
                  ...filter,
                  page,
                  limit: perPage,
                });
                dispatch(types.fetchedForecasts(forecasts));
                return forecasts;
              });
            })
          ),
          fetchKey ?? 'batch'
        )
      );
    } catch (e) {
      const message = errorMessageFactory(e);
      dispatch(showToast({ message, type: 'danger' }));
      throw e;
    }
  };
}

export function fetchForecastsForCalendar(
  partialFilter?: Partial<ForecastFilter>,
  fetchKey?: string
): Thunk<void> {
  return async (dispatch, getState) => {
    const state = getState();
    const calendarFilter = getForecastCalendarFilter(state);
    dispatch(
      fetchForecasts(
        {
          user_ids: calendarFilter.user_ids,
          completed: false,
          order: 'desc',
          ...partialFilter,
        },
        fetchKey
      )
    );
  };
}

export function fetchForecastsForCalendarSmartly(
  partialFilter?: Partial<ForecastFilter>,
  fetchKey: ForecastStorageKeys = 'active'
): Thunk<void> {
  return async (dispatch, getState) => {
    const state = getState();
    const key = getForecastFetchKeys(state)[fetchKey];
    const xhrStatus = xhrStatusQueryInfo(state.forecast.xhrStatus, { key });

    if (!xhrStatus.shouldFetch) {
      return;
    }
    dispatch(fetchForecastsForCalendar(partialFilter, key));
  };
}

export function resetForecastPaginationState(): Thunk<void> {
  return dispatch =>
    dispatch(
      paginationActions.setParams({
        limit: FORECAST_PAGE_LIMIT,
        order: 'desc',
        page: 1,
        sort: 'from',
      })
    );
}

export function resetForecastPaginationAndQueryState(): Thunk<void> {
  return dispatch => {
    dispatch(clearQuery());
    dispatch(resetForecastPaginationState());
  };
}

export function fetchPaginatedForecasts(partialFilter?: Partial<ForecastFilter>): Thunk<void> {
  return async (dispatch, getState) => {
    const state = getState();
    const key = JSON.stringify(partialFilter);
    const paginationState = getForecastPagination(state);

    await dispatch(fetchForecasts(partialFilter, key));

    const forecasts = getForecasts(state);
    const maybeHasMore = getMaybeHasMoreStatus(paginationState, forecasts.length);
    dispatch(paginationActions.setParams({ ...partialFilter, maybeHasMore }));
  };
}

const DEFAULT_FORECAST_SMART_FETCH_COUNT = 200;
export function fetchPaginatedForecastsSmartly(
  partialFilter?: Partial<ForecastFilter>
): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const paginationState = getForecastPagination(state);
    const query = getForecastQuery(state);
    const params: Partial<ForecastFilter> = {
      order: paginationState.order,
      page: paginationState.page,
      limit: DEFAULT_FORECAST_SMART_FETCH_COUNT,
      project_ids: query.projectIds,
      user_ids: getForecastQueryUserIds(state),
      since: query.since,
      until: query.until,
    };
    // Upcoming feature: filtering for backlog
    // if (partialFilter?.filter == null) {
    //   params.completed = query.showCompleted ? null : false;
    // }
    Object.assign(params, partialFilter);
    const key = JSON.stringify(params);
    const xhrStatus = xhrStatusQueryInfo(state.forecast.xhrStatus, { key });

    if (!xhrStatus.shouldFetch) {
      dispatch(paginationActions.setParams(params));
      return;
    }

    // When date filtering is active, fetch all pages to ensure we load all tasks in range
    if (query.since && query.until) {
      await dispatch(fetchForecastsInBatches([params], key));
      dispatch(paginationActions.setParams({ ...params, maybeHasMore: false }));
    } else {
      await dispatch(fetchPaginatedForecasts(params));
    }
  };
}

export function fetchForecastsForHourDraft(draft: EnrichedHourDraft): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const isRouteCalendar = getIsRouteCalendar(state);

    if (draft.forecast_id) {
      dispatch(fetchForecastId(draft.forecast_id));
    }

    if (isRouteCalendar) {
      dispatch(fetchForecastsForCalendarSmartly());
    } else {
      const dtDay = DateTime.fromISO(draft.day);
      const partialFilter = {
        user_ids: [draft.user.id],
        since: dtDay.minus({ month: 1 }).toISODate(),
        until: dtDay.plus({ month: 1 }).toISODate(),
        completed: false,
      };
      const fetchKey = new Date(draft.day).getMonth().toString();
      dispatch(fetchForecastsSmartly(partialFilter, fetchKey));
    }
  };
}

interface FetchForecastIdOptions {
  failSilently?: boolean;
}
export function fetchForecastId(
  id: number,
  { failSilently }: FetchForecastIdOptions = {}
): Thunk<Promise<void>> {
  return async dispatch => {
    try {
      const forecast = await dispatch(wrapFetching(forecastApi.getForecast(id)));
      dispatch(types.fetchedForecast(forecast));
    } catch (e) {
      // We want try "try" fetching a forecast that we might not have access to
      if (!failSilently) {
        throw e;
      }
    }
  };
}

export const deleteForecastIdInState = types.deleteForecastId;

export function deleteForecastId(forecastId: number): Thunk {
  return async (dispatch, getState) => {
    const forecastEntities = getForecastEntities(getState());
    const forecastCopy = { ...forecastEntities[forecastId] };

    try {
      dispatch(types.deleteForecastId(forecastId));
      await dispatch(wrapSaving(forecastApi.deleteForecast(forecastId)));
    } catch (error) {
      const message = errorMessageFactory(error);
      dispatch(showToast({ message, type: 'danger' }));
      dispatch(types.fetchedForecast(forecastCopy));
      throw error;
    }
  };
}

export function toggleCompleteOnForecastId(
  forecastId: number,
  context: TaskAnalyticsContext
): Thunk<Promise<Forecast | void>> {
  return (dispatch, getState) => {
    const forecast = getForecastEntities(getState())[forecastId];
    // @todo: Get the "clear done state" API cleaned up
    const completed_at = !forecast.completed_at ? new Date().toISOString() : '';
    const payload = { ...forecast, completed_at };
    return dispatch(saveForecast({ ...payload, context }));
  };
}

interface SaveForecastOptions {
  clearDraft?: boolean;
  /** Immediatly start a new forecast draft while saving occurs. This will NOT re-instate old draft on XHR error */
  initiateNewDraftImmediately?: boolean;
}

export function saveForecastDraft(options?: SaveForecastOptions): Thunk<Promise<Forecast | void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const draft = getForecastDraft(state);
    if (!draft) return Promise.reject();

    const permissions = getUserPermissions(getState());
    if (
      draft.userLacksProjectPermission &&
      permissions.canUpdate.forecasts &&
      draft.project != null
    ) {
      const projectId = draft.project.id;
      const promises = draft.users.map(user => dispatch(addUserToProject(user.id, projectId)));
      await dispatch(wrapFetching(Promise.all(promises), 'add-user-to-project'));
    }

    return dispatch(saveForecast(draft, options));
  };
}

/**
 *
 * @param forecast Forecast to save
 * @param options pass in any modifiers to the request, e.g. specify if we want to clear the draft immediately
 * @returns
 */
export function saveForecast(
  forecast: ForecastDraft,
  options?: SaveForecastOptions
): Thunk<Promise<Forecast | void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const { id, temporaryId } = forecast;
    const basedOnExistingForecast = temporaryId == null;
    const fallback = basedOnExistingForecast ? { ...getForecastEntities(state)[id] } : null;

    if (options?.clearDraft || options?.initiateNewDraftImmediately) {
      dispatch(clearForecastDraft());
    }
    if (options?.initiateNewDraftImmediately) {
      dispatch(
        initiateForecastDraftForActingUserId(
          forecast.context,
          forecast.users.map(u => u.id)
        )
      );
    }

    // inject locally ala optimistic update while awaiting XHR response
    dispatch(types.fetchedForecast(forecast));

    const context = finalizeTaskAnalyticsContext(forecast, state);

    try {
      const savedForecast = await dispatch(
        wrapFetching(
          forecastApi.saveForecast({
            ...pick(
              forecast,
              'label_ids',
              'title',
              'description',
              'from',
              'to',
              'completed_at',
              'estimated_minutes'
            ),
            project_id: forecast.project?.id,
            users: forecast.users.map(u => pick(u, 'id')),
            id: basedOnExistingForecast ? id : null,
            description: forecast.description ?? '',
            context,
          })
        )
      );
      dispatch(types.fetchedForecast(savedForecast));
      return savedForecast;
    } catch (error) {
      // Replace optimistic local update with previous version of XHR fails
      if (basedOnExistingForecast && fallback) {
        dispatch(types.fetchedForecast(fallback));
      }
      // Reinstate draft
      dispatch(setForecastDraft({ ...forecast, openFor: 'editing' }));

      if (getApiValidationErrors(error)?.project_id != null) {
        dispatch(types.updateForecastDraft({ userLacksProjectPermission: true }));
      } else {
        const message = errorMessageFactory(error);
        dispatch(showToast({ message, type: 'danger' }));
        throw error;
      }
    } finally {
      if (!basedOnExistingForecast) {
        // if not based on existing forecast, remove the optimistic entry
        dispatch(types.deleteForecastId(temporaryId));
      }
    }
  };
}

export function editForecastId(
  id: number,
  context: TaskAnalyticsContext,
  diff?: Partial<ForecastDraft>
): Thunk<void> {
  return (dispatch, getState) => {
    const forecast = getForecastEntities(getState())[id];
    if (forecast) {
      dispatch(setForecastDraft({ ...forecast, openFor: 'editing', ...diff, context }));
    }
  };
}

export function setForecastDraft(forecast: ForecastDraft): Thunk<void> {
  return dispatch => {
    if (forecast.openFor === 'editing') {
      const project = forecast.project?.active ? forecast.project : null;
      dispatch(types.setForecastDraft({ ...forecast, project }));
    }
    dispatch(types.setForecastDraft(forecast));
  };
}

export function viewForecastId(id: number, context: TaskAnalyticsContext): Thunk<void> {
  return (dispatch, getState) => {
    const forecast = getForecastEntities(getState())[id];
    if (forecast) {
      dispatch(setForecastDraft({ ...forecast, context }));
    }
  };
}
/**
 * Initiate Forecast draft for currently active user in Calendar-context,
 * or optionally, pass a userId.
 */
export function initiateForecastDraftForActingUserId(
  context: TaskAnalyticsContext,
  userId?: number | null | Array<number>,
  data?: Partial<Forecast> & { projectId?: number }
): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const actingCalendarUserId = typeof userId === 'undefined' ? getCalendarUserId(state) : null;
    userId ??= actingCalendarUserId;
    const userIds = Array.isArray(userId) ? userId : [userId].filter(notNull);
    const users = userIds.map(id => getUserEntities(state)[id]);
    const canCreateForecasts = getUserPermissions(state).canCreate.forecasts;

    if (!canCreateForecasts) {
      dispatch(
        showToast({
          message: "You don't have permission to create Tasks",
          type: 'warning',
        })
      );
      return;
    }

    const { projectId, ...rest } = data || {};

    const now = DateTime.now();
    // Closest Friday
    const to = getNextInstanceOfISODay('5');
    let from = now.toISODate();
    const isWeekend = now.weekday === 6 || now.weekday === 7;

    if (isWeekend) {
      // Closest Monday
      from = getNextInstanceOfISODay('1');
    }

    const finalPayload = generateForecastDraft({
      estimated_minutes: 60 * 8,
      logged_duration: durationFromHours(0),
      from,
      to,
      users,
      ...rest,
      context,
    });

    if (projectId != null) {
      const projects = getProjectById(getState());
      const project = extractProjectBase(projects[projectId]!);
      finalPayload.project = project;
    }

    dispatch(setForecastDraft(finalPayload));
  };
}

export function resetForecastDraftForId(id?: number): Thunk<void> {
  return (dispatch, getState) => {
    const draft = getForecastDraft(getState());
    if (id && draft) {
      const forecast = getForecastEntities(getState())[id];
      dispatch(setForecastDraft({ ...forecast, context: draft.context }));
    }
  };
}

/**
 * Updates the forecast draft with a new project.
 * @todo Probably some smartness for fetching project (we'll likely need project tasks here soon)
 */
export function updateForecastDraftProjectId(projectId: number): Thunk<unknown> {
  return (dispatch, getState) => {
    const projects = getProjectById(getState());
    const project = extractProjectBase(projects[projectId]!);
    dispatch(
      types.updateForecastDraft({ project, label_ids: [], userLacksProjectPermission: undefined })
    );
  };
}

export function updateForecastDraftAssigneeIds(newUserIds: Array<number>): Thunk<unknown> {
  return (dispatch, getState) => {
    const state = getState();
    const userMap = getUserEntities(state);
    const users = getForecastDraft(state)?.users.slice() || [];

    remove(users, user => !newUserIds.includes(user.id));
    newUserIds.forEach(userId => {
      if (users.find(u => u.id === userId)) {
        return;
      }
      const user = userMap[userId];
      if (user) {
        users.push(user);
      }
    });

    dispatch(types.updateForecastDraft({ users, userLacksProjectPermission: undefined }));
    users.map(user => dispatch(fetchInitialProjects(user.id)));
  };
}

export function updateForecastDraft(payload: Partial<ForecastDraft>): Thunk<unknown> {
  return dispatch => {
    dispatch(types.updateForecastDraft(payload));
  };
}

export interface MoveForecastOptions {
  startDate: string;
  endDate: string;
  assignee?: { from: number | null; to: number | null };
  projectArchived?: boolean;
  project_id?: number;
}
export function moveForecast(
  forecastId: number,
  options: MoveForecastOptions,
  context: TaskAnalyticsContext
): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const forecast = getForecastEntities(state)[forecastId];
    if (!forecast) return;
    const newForecast: ForecastDraft = {
      ...forecast,
      from: options.startDate,
      to: options.endDate,
      context,
    };
    if (options.assignee != null) {
      const { from: fromUserId, to: toUserId } = options.assignee;
      const newUser = toUserId === null ? null : getUserEntities(state)[toUserId];
      const users = forecast.users.slice();
      remove(users, u => u.id === fromUserId);
      if (newUser != null && !forecast.users.find(user => user.id === newUser.id)) {
        users.push(newUser);
      }
      newForecast.users = users;
    }
    if (options.project_id && !options.projectArchived) {
      const projects = getProjectById(getState());
      const project = extractProjectBase(projects[options.project_id]!);
      newForecast.project = project;
    }
    if (options.projectArchived) {
      newForecast.project = null;
    }
    dispatch(saveForecast(newForecast));
  };
}

export function copyForecast(
  forecastId: number,
  options: MoveForecastOptions,
  context: TaskAnalyticsContext
): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const originalForecast = getForecastEntities(state)[forecastId];
    if (!originalForecast) return;
    const newForecast: ForecastDraft = {
      ...generateForecastDraft({ users: originalForecast.users, context }),
      ...omit(originalForecast, 'id', 'completed_at'),
      completed_at: null,
      from: options.startDate,
      to: options.endDate,
    };
    if (options.assignee != null) {
      const { to: toUserId } = options.assignee;
      const newUser = toUserId === null ? null : getUserEntities(state)[toUserId];
      let users = newForecast.users.slice();
      if (newUser == null) {
        users = [];
      } else {
        if (users.length > 1 && !users.find(user => user.id === newUser.id)) {
          users.push(newUser);
        } else {
          users = [newUser];
        }
      }
      newForecast.users = users;
    }
    if (options.project_id) {
      const projects = getProjectById(getState());
      const project = extractProjectBase(projects[options.project_id]!);
      newForecast.project = project;
    }
    dispatch(saveForecast(newForecast));
  };
}

export const clearAllForecasts = types.clearAllForecasts;

/**
 * Initiates a new hour draft based on a forecast.
 */
export function createHourDraftFromForecastId(
  forecastId: number,
  context: HourAnalyticsContext,
  hourSeed?: HourDraftSeed,
  options?: {
    /** Who are we creating the time entry for? */
    userId?: number;
    /** Save the hour immediately, bypassing the HourEditor draft step */
    createImmediately?: boolean;
    /** if `createImmediately`, also run a timer? */
    startTimer?: boolean;
  }
): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const forecast = getEnrichedForecasts(state).find(f => f.id === forecastId);
    const today = DateTime.now().toISODate();
    const isInFuture = isAfterDay(hourSeed?.day ?? today, today);
    const user = forecast?.users?.find(u => u.id === options?.userId) ?? getUser(state);

    const secondsOfWorkInDay =
      (forecast?.allocationByUser[user.id]?.dailyWorkMinutesRequiredToCompleteWithinRange || 0) *
      60;

    const duration = calculateDuration(secondsOfWorkInDay);

    const partialDraft: HourDraftSeed = {
      forecast_id: forecastId,
      day: hourSeed?.day ?? today,
      note: forecast?.title,
      project: forecast?.project?.id,
      label_ids: forecast?.label_ids,
    };

    // If we're not starting a timer from scratch..
    if (!options?.startTimer) {
      // ... and the entry is for today or in the past, prefill the duration
      if (!isInFuture) {
        partialDraft.hours = duration.hours;
        partialDraft.minutes = duration.minutes;
        partialDraft.seconds = duration.seconds;
        // .. but set estimated duration if we're creating a time entry in the future
      } else {
        partialDraft.estimated_duration = duration;
      }
    }

    const draftPayload = {
      ...partialDraft,
      ...hourSeed,
      user,
    };

    if (options?.createImmediately) {
      const savePayload = hourDraftFactory(draftPayload, context);
      getHoursActions().then(({ saveHour, toggleTimerOnHourId }) => {
        dispatch(
          saveHour(savePayload, {
            refreshForecastId: forecastId,
            createImmediately: true,
          })
        ).then(res => {
          if (options?.startTimer) {
            dispatch(toggleTimerOnHourId(res.id, context));
          }
        });
      });
    } else {
      getHoursActions().then(({ createHourDraft }) => {
        dispatch(createHourDraft(context, draftPayload));
      });
    }
  };
}

export function batchUpdateForecastSelection(): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const selections = getForecastSelections(state);
    const forecasts = getForecastEntities(state);

    const updatedForecasts = selections.forecastIds.map(forecastId => {
      const forecast = forecasts[forecastId];
      return {
        ...forecast,
        completed_at: selections.completedState ? new Date().toISOString() : null,
      };
    });

    dispatch(fetchedForecasts(updatedForecasts));

    dispatch(wrapFetching(forecastApi.bulkUpdateForecasts(updatedForecasts), 'batch-update'))
      .then(res => {
        dispatch(batchSelectionActions.clearSelection());
        const numUpdated = res.updated_ids.length;
        dispatch(
          showToast({
            message: `Updated ${numUpdated} ${numUpdated === 1 ? 'task' : 'tasks'}`,
            type: 'success',
          })
        );
      })
      .catch(error => {
        const originalForecasts = selections.forecastIds.map(f => forecasts[f]);
        dispatch(showToast({ message: errorMessageFactory(error), type: 'danger' }));
        dispatch(fetchedForecasts(originalForecasts));
      });
  };
}

export function batchDeleteForecastSelection(): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const selections = getForecastSelections(state);
    const forecasts = getForecastEntities(state);
    dispatch(deleteForecastIds(selections.forecastIds));
    dispatch(wrapFetching(forecastApi.bulkDeleteForecasts(selections.forecastIds), 'batch-delete'))
      .then(res => {
        dispatch(batchSelectionActions.clearSelection());
        const numUpdated = res.deleted_ids.length;
        dispatch(
          showToast({
            message: `Deleted ${numUpdated} ${numUpdated === 1 ? 'task' : 'tasks'}`,
            type: 'success',
          })
        );
      })
      .catch(error => {
        const originalForecasts = selections.forecastIds.map(f => forecasts[f]);
        dispatch(showToast({ message: errorMessageFactory(error), type: 'danger' }));
        dispatch(fetchedForecasts(originalForecasts));
      });
  };
}

export function setQuery(changes: Partial<types.ForecastQuery>): Thunk {
  return async (dispatch, getState) => {
    const query = {
      ...getForecastQuery(getState()),
      ...changes,
    };
    if (query.onlyShowProjectAssignees) {
      // If both teams and "Only show project assignees" are active, clear teams (project assignees takes priority)
      if (query.teamIds != null) {
        query.teamIds = null;
      }
      if (query.projectIds.length > PROJECT_ASSIGNEES_FILTER_LIMIT) {
        if (changes.projectIds) {
          query.onlyShowProjectAssignees = false;
        } else {
          query.projectIds = query.projectIds.slice(0, PROJECT_ASSIGNEES_FILTER_LIMIT);
        }
        // query.onlyShowProjectAssignees = false;
        // query.userIds = []; // Clear user selection to show "Anyone"
        // query.teamIds = null; // Clear teams as well
      }
    }
    if (changes.onlyShowProjectAssignees === false && !changes.userIds) {
      query.userIds = []; // Clear user selection to show "Anyone"
      query.teamIds = null; // Clear teams as well
    }
    if (changes.onlyShowProjectAssignees) {
      // Reset userIds and teamIds when switching to "Project Assignees" filter
      query.userIds = null;
      query.teamIds = null;
    }

    dispatch(types.setQuery_(query));

    if (query.onlyShowProjectAssignees) {
      await dispatch(
        fetchFullProjectsWithUsersIfNeeded(
          query.projectIds.slice(0, PROJECT_ASSIGNEES_FILTER_LIMIT)
        )
      );
      const newQuery = getForecastQuery(getState());
      if (newQuery.userIds && newQuery.userIds.length > 0) {
        const availableUsers = getFilteredForecastUsers(getState());
        const newUserIds =
          newQuery.userIds?.filter(
            userId => userId == null || availableUsers.some(user => user.id === userId)
          ) ?? [];
        if (newUserIds.length !== newQuery.userIds.length) {
          // If we filtered out some userIds, update the query
          dispatch(types.setQuery_({ userIds: newUserIds }));
        }
      }
    }
  };
}
